/* ============================================================================
 * Movatic WMS - Coletor | Navegacao por niveis (ADR-0034 F2b)
 * Arquivo: src/mobile/nav/coletor-nav.ts
 * Autor: CesarFilho 932048016
 * ----------------------------------------------------------------------------
 * A arvore do COLETOR: 4 areas de 1o nivel, cada uma com os seus modulos.
 * NAO duplica o menu do desktop -- reorganiza por frequencia operacional.
 * Cada modulo tem um ESTADO (mapa vivo do progresso, banco de provas):
 *   - "ligado"     : funciona de verdade no coletor (tem rota /m/* operacional)
 *   - "por-portar" : existe no desktop, ainda sem ecra no coletor (cinza)
 *   - "ecossistema": produto do ecossistema ainda nao ligado ao frontend (roxo)
 * So' "ligado" e' clicavel; o resto e' visivel mas inerte.
 * ========================================================================== */

import type { LucideIcon } from "lucide-react";
import {
    ArrowLeftRight, Boxes, Building2, Calculator, ClipboardList, Factory,
    Inbox, Layers, LayoutDashboard, MapPin, Megaphone, Package,
    Printer, Receipt, ScanLine, Ship, ShoppingCart, TrendingUp,
    Truck, Users, Wrench,
} from "lucide-react";

export type ColetorEstado = "ligado" | "por-portar" | "ecossistema";

export type ColetorModulo = {
    id: string;
    label: string;
    icon: LucideIcon;
    estado: ColetorEstado;
    /** rota /m/* -- so' presente quando estado === "ligado" */
    to?: string;
    /** legenda curta opcional */
    hint?: string;
};

export type ColetorArea = {
    id: string;
    label: string;
    icon: LucideIcon;
    /** roxo quando a area inteira e' ecossistema */
    eco?: boolean;
    modulos: ColetorModulo[];
};

export const COLETOR_AREAS: ColetorArea[] = [
    {
        id: "operacao",
        label: "Operacao",
        icon: ClipboardList,
        modulos: [
            { id: "scan", label: "Scan", icon: ScanLine, estado: "ligado", to: "/m/scan", hint: "picar codigo" },
            { id: "consulta", label: "Consulta", icon: Boxes, estado: "ligado", to: "/m/lookup", hint: "estoque + foto" },
            { id: "tarefas", label: "Tarefas", icon: ClipboardList, estado: "ligado", to: "/m/tasks", hint: "fila de trabalho" },
            { id: "recebimento", label: "Recebimento", icon: Inbox, estado: "por-portar", hint: "no PC" },
            { id: "expedicao", label: "Expedicao", icon: Ship, estado: "por-portar", hint: "no PC" },
            { id: "movimentar", label: "Movimentar", icon: ArrowLeftRight, estado: "por-portar", hint: "no PC" },
            { id: "contagem", label: "Contagem", icon: Calculator, estado: "por-portar", hint: "no PC" },
            { id: "impressao", label: "Impressao", icon: Printer, estado: "por-portar", hint: "no PC" },
        ],
    },
    {
        id: "gestao",
        label: "Gestao",
        icon: TrendingUp,
        modulos: [
            { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, estado: "por-portar", hint: "no PC" },
            { id: "compras", label: "Compras", icon: ShoppingCart, estado: "por-portar", hint: "no PC" },
            { id: "orders", label: "Orders", icon: Receipt, estado: "por-portar", hint: "no PC" },
            { id: "mes", label: "MES", icon: Factory, estado: "por-portar", hint: "no PC" },
            { id: "yard", label: "Yard", icon: MapPin, estado: "por-portar", hint: "no PC" },
            { id: "copack", label: "Copack", icon: Factory, estado: "por-portar", hint: "no PC" },
            { id: "frete", label: "Frete", icon: Truck, estado: "por-portar", hint: "no PC" },
            { id: "marketing", label: "Marketing", icon: Megaphone, estado: "por-portar", hint: "no PC" },
            { id: "assembly", label: "Assembly", icon: Wrench, estado: "por-portar", hint: "no PC" },
        ],
    },
    {
        id: "cadastros",
        label: "Cadastros",
        icon: Layers,
        modulos: [
            { id: "catalogo", label: "Catalogo", icon: Package, estado: "por-portar", hint: "no PC" },
            { id: "core", label: "Estrutura", icon: Building2, estado: "por-portar", hint: "no PC" },
            { id: "parceiros", label: "Parceiros", icon: Users, estado: "por-portar", hint: "no PC" },
            { id: "workforce", label: "Workforce", icon: Users, estado: "por-portar", hint: "no PC" },
        ],
    },
    {
        id: "ecossistema",
        label: "Ecossistema",
        icon: Boxes,
        eco: true,
        modulos: [
            { id: "mfc", label: "MFC", icon: Wrench, estado: "ecossistema", hint: "a ligar" },
            { id: "ai-flow", label: "AI Flow", icon: Wrench, estado: "ecossistema", hint: "a ligar" },
            { id: "simu", label: "SIMU", icon: Boxes, estado: "ecossistema", hint: "a ligar" },
            { id: "catalogo-global", label: "Catalogo global", icon: Package, estado: "ecossistema", hint: "a ligar" },
        ],
    },
];

/** Conta modulos "ligado" numa area (para o cabecalho "N prontos"). */
export function contarProntos(area: ColetorArea): number {
    return area.modulos.filter((m) => m.estado === "ligado").length;
}

export function getArea(id: string): ColetorArea | undefined {
    return COLETOR_AREAS.find((a) => a.id === id);
}

export default COLETOR_AREAS;
